import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { encrypt, decrypt } from '../utils/encryption';

/**
 * 패스워드 관련 Firebase 서비스
 */
export const passwordService = {
  
  /**
   * 사용자의 패스워드 목록을 로드
   * @param {string} userUid - 사용자 UID
   * @returns {Promise<Array>} 복호화된 패스워드 목록
   */
  async getPasswords(userUid) {
    if (!userUid) {
      throw new Error('사용자 UID가 필요합니다');
    }

    try {
      if (import.meta.env.DEV) {
        console.log('📋 패스워드 컬렉션 조회 중...');
      }
      
      const querySnapshot = await getDocs(collection(db, `users/${userUid}/passwords`));
      const passwordList = [];

      // forEach → for...of 변경 (async/await 호환)
      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        if (import.meta.env.DEV) {
          console.log('🔐 패스워드 복호화 중...');
        }
        
        try {
          // 암호화된 데이터 복호화 (await 추가)
          const decryptedPassword = {
            id: doc.id,
            title: await decrypt(data.title, userUid),
            username: await decrypt(data.username, userUid),
            password: await decrypt(data.password, userUid),
            website: data.website ? await decrypt(data.website, userUid) : '',
            category: data.category ? await decrypt(data.category, userUid) : '',
            tags: data.tags ? JSON.parse(await decrypt(data.tags, userUid)) : [],
            notes: data.notes ? await decrypt(data.notes, userUid) : '',
            isFavorite: data.isFavorite || false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            itemType: 'password'
          };
          
          passwordList.push(decryptedPassword);
          if (import.meta.env.DEV) {
            console.log('✅ 복호화 완료');
          }
        } catch (error) {
          if (error.message === 'LEGACY_DATA_DETECTED') {
            console.warn('⚠️ 레거시 데이터 건너뜀:', doc.id);
            console.info('💡 이 패스워드는 이전 버전으로 암호화되어 자동으로 제외됩니다.');
          } else {
            console.error('❌ 복호화 실패:', error.message);
          }
        }
      }

      if (import.meta.env.DEV) {
        console.log('📊 총 패스워드 개수:', passwordList.length);
      }
      return passwordList;
      
    } catch (error) {
      console.error('❌ 패스워드 로드 실패:', error);
      throw new Error('패스워드 목록을 불러오는데 실패했습니다');
    }
  },

  /**
   * 새 패스워드를 추가
   * @param {string} userUid - 사용자 UID
   * @param {Object} passwordData - 패스워드 데이터
   * @returns {Promise<Object>} 생성된 패스워드 객체
   */
  async addPassword(userUid, passwordData) {
    if (import.meta.env.DEV || import.meta.env.VITE_APP_DEBUG === 'true') {
      console.log('🔐 패스워드 저장 시작...');
    }

    if (!passwordData || !userUid) {
      console.error('❌ 필수 데이터 누락');
      throw new Error('패스워드 데이터와 사용자 UID가 필요합니다');
    }

    const { title, username, password, website, category, tags, notes } = passwordData;

    // 필수 필드 검증
    if (!title?.trim() || !username?.trim() || !password?.trim()) {
      console.error('❌ 필수 필드 누락');
      throw new Error('제목, 사용자명, 패스워드는 필수 입력 항목입니다');
    }

    try {
      if (import.meta.env.DEV || import.meta.env.VITE_APP_DEBUG === 'true') {
        console.log('🔐 새 패스워드 암호화 중...');
      }
      
      // 데이터 암호화 (await 추가)
      const encryptedData = {
        title: await encrypt(title.trim(), userUid),
        username: await encrypt(username.trim(), userUid),
        password: await encrypt(password.trim(), userUid),
        website: website?.trim() ? await encrypt(website.trim(), userUid) : '',
        category: category?.trim() ? await encrypt(category.trim(), userUid) : '',
        tags: tags && tags.length > 0 ? await encrypt(JSON.stringify(tags), userUid) : '',
        notes: notes?.trim() ? await encrypt(notes.trim(), userUid) : '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (import.meta.env.DEV || import.meta.env.VITE_APP_DEBUG === 'true') {
        console.log('✅ 암호화 완료');
        console.log('💾 Firestore에 저장 중...');
      }
      
      // Firestore에 저장
      const docRef = await addDoc(
        collection(db, `users/${userUid}/passwords`), 
        encryptedData
      );

      if (import.meta.env.DEV || import.meta.env.VITE_APP_DEBUG === 'true') {
        console.log('✅ 패스워드 저장 완료:', docRef.id);
      }
      
      // 새로 생성된 패스워드 객체 반환 (복호화된 형태)
      return {
        id: docRef.id,
        title: title.trim(),
        username: username.trim(),
        password: password.trim(),
        website: website?.trim() || '',
        category: category?.trim() || '',
        tags: tags || [],
        notes: notes?.trim() || '',
        createdAt: encryptedData.createdAt,
        updatedAt: encryptedData.updatedAt
      };
      
    } catch (error) {
      console.error('❌ 패스워드 저장 실패:', error.message);
      throw new Error('패스워드 저장에 실패했습니다');
    }
  },

  /**
   * 패스워드를 수정
   * @param {string} userUid - 사용자 UID
   * @param {string} passwordId - 수정할 패스워드 ID
   * @param {Object} updatedData - 수정할 데이터
   * @returns {Promise<Object>} 수정된 패스워드 객체
   */
  async updatePassword(userUid, passwordId, updatedData) {
    if (!passwordId || !userUid || !updatedData) {
      throw new Error('패스워드 ID, 사용자 UID, 수정 데이터가 필요합니다');
    }

    try {
      if (import.meta.env.DEV) {
        console.log('🔐 패스워드 수정 중...');
      }
      
      // 모든 필드를 암호화하여 저장 (전체 업데이트)
      const encryptedData = {};
      
      // isFavorite만 업데이트하는 경우
      if (Object.keys(updatedData).length === 1 && 'isFavorite' in updatedData) {
        encryptedData.isFavorite = updatedData.isFavorite;
      } else {
        // 전체 필드 업데이트
        encryptedData.title = await encrypt(updatedData.title.trim(), userUid);
        encryptedData.username = await encrypt(updatedData.username.trim(), userUid);
        encryptedData.password = await encrypt(updatedData.password.trim(), userUid);
        encryptedData.website = updatedData.website?.trim() ? 
          await encrypt(updatedData.website.trim(), userUid) : '';
        encryptedData.category = updatedData.category?.trim() ? 
          await encrypt(updatedData.category.trim(), userUid) : '';
        encryptedData.tags = updatedData.tags && updatedData.tags.length > 0 ? 
          await encrypt(JSON.stringify(updatedData.tags), userUid) : '';
        encryptedData.notes = updatedData.notes?.trim() ? 
          await encrypt(updatedData.notes.trim(), userUid) : '';
        encryptedData.isFavorite = updatedData.isFavorite || false;
      }
      
      encryptedData.updatedAt = new Date();

      // Firestore 업데이트
      await updateDoc(doc(db, `users/${userUid}/passwords`, passwordId), encryptedData);
      
      if (import.meta.env.DEV) {
        console.log('✅ 패스워드 수정 완료');
      }
      
      // 수정된 패스워드 객체 반환 (복호화된 형태로 정확히 반환)
      if (Object.keys(updatedData).length === 1 && 'isFavorite' in updatedData) {
        // isFavorite만 업데이트한 경우 간단한 객체 반환
        return {
          id: passwordId,
          isFavorite: updatedData.isFavorite,
          updatedAt: encryptedData.updatedAt
        };
      } else {
        return {
          id: passwordId,
          title: updatedData.title.trim(),
          username: updatedData.username.trim(),
          password: updatedData.password.trim(),
          website: updatedData.website?.trim() || '',
          category: updatedData.category?.trim() || '',
          tags: updatedData.tags || [],
          notes: updatedData.notes?.trim() || '',
          isFavorite: updatedData.isFavorite || false,
          createdAt: updatedData.createdAt, // 원본 생성일 유지
          updatedAt: encryptedData.updatedAt
        };
      }
      
    } catch (error) {
      console.error('❌ 패스워드 수정 실패:', error);
      throw new Error('패스워드 수정에 실패했습니다');
    }
  },

  /**
   * 패스워드를 삭제
   * @param {string} userUid - 사용자 UID
   * @param {string} passwordId - 삭제할 패스워드 ID
   * @returns {Promise<void>}
   */
  async deletePassword(userUid, passwordId) {
    if (!passwordId || !userUid) {
      throw new Error('패스워드 ID와 사용자 UID가 필요합니다');
    }

    try {
      if (import.meta.env.DEV) {
        console.log('🗑️ 패스워드 삭제 중...');
      }
      
      await deleteDoc(doc(db, `users/${userUid}/passwords`, passwordId));
      
      if (import.meta.env.DEV) {
        console.log('✅ 패스워드 삭제 완료');
      }
      
    } catch (error) {
      console.error('❌ 패스워드 삭제 실패:', error);
      throw new Error('패스워드 삭제에 실패했습니다');
    }
  },

  /**
   * 사용자의 모든 패스워드를 삭제
   * @param {string} userUid - 사용자 UID
   * @returns {Promise<number>} 삭제된 패스워드 개수
   */
  async deleteAllPasswords(userUid) {
    if (!userUid) {
      throw new Error('사용자 UID가 필요합니다');
    }

    try {
      if (import.meta.env.DEV) {
        console.log('🗑️ 모든 패스워드 삭제 시작...');
      }
      
      // 모든 패스워드 문서 조회
      const querySnapshot = await getDocs(collection(db, `users/${userUid}/passwords`));
      
      if (querySnapshot.empty) {
        if (import.meta.env.DEV) {
          console.log('📭 삭제할 패스워드가 없습니다');
        }
        return 0;
      }

      // 모든 문서 삭제
      const deletePromises = [];
      querySnapshot.forEach((document) => {
        deletePromises.push(deleteDoc(doc(db, `users/${userUid}/passwords`, document.id)));
      });

      await Promise.all(deletePromises);
      
      const deletedCount = querySnapshot.size;
      if (import.meta.env.DEV) {
        console.log('✅ 모든 패스워드 삭제 완료:', deletedCount + '개');
      }
      
      return deletedCount;
      
    } catch (error) {
      console.error('❌ 모든 패스워드 삭제 실패:', error);
      throw new Error('모든 패스워드 삭제에 실패했습니다');
    }
  },

  /**
   * 이미 암호화된 패스워드 데이터를 직접 저장 (가져오기 기능용)
   * @param {string} userUid - 사용자 UID
   * @param {Object} encryptedData - 이미 암호화된 패스워드 데이터
   * @returns {Promise<Object>} 생성된 패스워드 객체
   */
  async importPassword(userUid, encryptedData) {
    if (!userUid || !encryptedData) {
      throw new Error('사용자 UID와 암호화된 데이터가 필요합니다');
    }

    try {
      if (import.meta.env.DEV) {
        console.log('📥 암호화된 패스워드 가져오기...');
      }

      // 이미 암호화된 데이터이므로 그대로 저장
      const passwordData = {
        title: encryptedData.title,
        username: encryptedData.username,
        password: encryptedData.password,
        website: encryptedData.url || encryptedData.website || '',
        category: encryptedData.category || '',
        tags: encryptedData.tags ? JSON.stringify(encryptedData.tags) : '[]',
        notes: encryptedData.notes || '',
        createdAt: encryptedData.createdAt || new Date().toISOString(),
        updatedAt: encryptedData.updatedAt || new Date().toISOString()
      };

      // Firestore에 저장
      const docRef = await addDoc(collection(db, `users/${userUid}/passwords`), passwordData);
      
      if (import.meta.env.DEV) {
        console.log('✅ 암호화된 패스워드 가져오기 완료');
      }

      // 복호화하여 반환 (UI 표시용)
      try {
        const decryptedPassword = {
          id: docRef.id,
          title: await decrypt(passwordData.title, userUid),
          username: await decrypt(passwordData.username, userUid),
          password: await decrypt(passwordData.password, userUid),
          website: passwordData.website ? await decrypt(passwordData.website, userUid) : '',
          category: passwordData.category ? await decrypt(passwordData.category, userUid) : '',
          tags: passwordData.tags ? JSON.parse(await decrypt(passwordData.tags, userUid)) : [],
          notes: passwordData.notes ? await decrypt(passwordData.notes, userUid) : '',
          createdAt: passwordData.createdAt,
          updatedAt: passwordData.updatedAt
        };
        
        return decryptedPassword;
      } catch (decryptError) {
        console.error('❌ 가져온 데이터 복호화 실패:', decryptError);
        // 복호화 실패 시 ID만 반환
        return { id: docRef.id, error: '복호화 실패' };
      }
      
    } catch (error) {
      console.error('❌ 패스워드 가져오기 실패:', error);
      throw new Error('패스워드 가져오기에 실패했습니다');
    }
  },

  /**
   * 암호화된 패스워드 데이터를 그대로 가져오기 (내보내기용)
   * @param {string} userUid - 사용자 UID
   * @returns {Promise<Array>} 암호화된 패스워드 목록
   */
  async getEncryptedPasswords(userUid) {
    if (!userUid) {
      throw new Error('사용자 UID가 필요합니다');
    }

    try {
      if (import.meta.env.DEV) {
        console.log('🔐 암호화된 패스워드 목록 조회 중...');
      }
      
      const querySnapshot = await getDocs(collection(db, `users/${userUid}/passwords`));
      const encryptedPasswordList = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        encryptedPasswordList.push({
          id: doc.id,
          ...data
        });
      });

      if (import.meta.env.DEV) {
        console.log('📊 암호화된 패스워드 개수:', encryptedPasswordList.length);
      }
      
      return encryptedPasswordList;
      
    } catch (error) {
      console.error('❌ 암호화된 패스워드 로드 실패:', error);
      throw new Error('암호화된 패스워드 목록을 불러오는데 실패했습니다');
    }
  },

  /**
   * 패스워드 검색
   * @param {Array} passwords - 패스워드 목록
   * @param {string} searchTerm - 검색어
   * @returns {Array} 검색 결과
   */
  searchPasswords(passwords, searchTerm) {
    if (!searchTerm?.trim()) {
      return passwords;
    }

    const term = searchTerm.trim().toLowerCase();
    
    return passwords.filter(password => 
      password.title.toLowerCase().includes(term) ||
      password.username.toLowerCase().includes(term) ||
      (password.website && password.website.toLowerCase().includes(term))
    );
  },

  /**
   * 패스워드 강도 체크
   * @param {string} password - 체크할 패스워드
   * @returns {Object} 강도 정보
   */
  checkPasswordStrength(password) {
    if (!password) {
      return { score: 0, message: '패스워드를 입력해주세요' };
    }

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    // 점수 계산
    Object.values(checks).forEach(check => {
      if (check) score++;
    });

    // 강도 메시지
    let message;
    if (score < 2) {
      message = '매우 약함';
    } else if (score < 3) {
      message = '약함';
    } else if (score < 4) {
      message = '보통';
    } else if (score < 5) {
      message = '강함';
    } else {
      message = '매우 강함';
    }

    return {
      score,
      message,
      checks
    };
  }
};

export default passwordService;